import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useBoxplotQuery, BoxplotStatistic } from '@/queries/useBoxplotQuery';
import { useQueryBounds, useQueryType } from '@/queries/api';
import { useSensorKitConfigs } from "@/sensorKitConfigs/api";
import { SensorKitFilters } from "@/sensorKits/api";
import { useBoxPlotChart } from "./useBoxPlotChart";
import { useDateRange } from "@/dateRanges/DateRangeContext";

interface SensorBoxPlotProps {
  filters: SensorKitFilters;
  queryType: string;
  orderBy?: string;
  orderDirection?: string;
}

// Inner component for filter controls
const FilterControls = ({
  filterOutOfBounds,
  setFilterOutOfBounds,
  selectedStatistic,
  setSelectedStatistic
}: {
  filterOutOfBounds: boolean;
  setFilterOutOfBounds: (value: boolean) => void;
  selectedStatistic: BoxplotStatistic;
  setSelectedStatistic: (value: BoxplotStatistic) => void;
}) => (
  <div className="mb-4 flex items-center gap-2 flex-wrap">
    <Checkbox
      checked={filterOutOfBounds}
      onCheckedChange={setFilterOutOfBounds}
    />
    <span className="text-sm">Only show sensor kits with</span>
    <Select
      value={selectedStatistic}
      onValueChange={(value) => setSelectedStatistic(value as BoxplotStatistic)}
      disabled={!filterOutOfBounds}
    >
      <SelectTrigger className="w-[140px] h-8">
        <SelectValue placeholder="Select statistic" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="min_data">Min</SelectItem>
        <SelectItem value="p25_data">P25</SelectItem>
        <SelectItem value="median_data">Median</SelectItem>
        <SelectItem value="p75_data">P75</SelectItem>
        <SelectItem value="max_data">Max</SelectItem>
      </SelectContent>
    </Select>
    <span className="text-sm">out of bounds</span>
  </div>
);

// Inner component for skipped configs notice
const SkippedConfigsNotice = ({
  skippedConfigs
}: {
  skippedConfigs: Array<{ config_id: number; config_name?: string; reason: string }>
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (skippedConfigs.length === 0) return null;

  return (
    <div className="mb-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between p-0 h-auto hover:bg-transparent"
            >
              <p className="text-sm font-medium text-yellow-800">
                Some sensor kit configs were skipped ({skippedConfigs.length})
              </p>
              {isOpen ? (
                <ChevronDownIcon className="h-4 w-4 text-yellow-800" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-yellow-800" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <ul className="text-sm text-yellow-700 space-y-1">
              {skippedConfigs.map((config) => (
                <li key={config.config_id}>
                  {config.config_name || `Config ID ${config.config_id}`}: {config.reason}
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};

// Inner component for chart container
const ChartContainer = ({
  chartRef,
  sensorData
}: {
  chartRef: React.RefObject<HTMLDivElement>;
  sensorData: any[]
}) => {
  if (sensorData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white rounded-lg shadow">
        <p className="text-gray-500">No sensor kits match the current filters</p>
      </div>
    );
  }

  return (
    <div
      ref={chartRef}
      className="w-full h-[600px] bg-white rounded-lg shadow"
    />
  );
};

// Inner component that calls all the hooks
const SensorBoxPlotContent = ({ filters, queryType, orderBy, orderDirection }: SensorBoxPlotProps) => {
  // Get date range from context
  const { dateRange } = useDateRange();

  // Memoize dates to prevent infinite re-queries
  const start = useMemo(() => dateRange.value.from(), [dateRange]);
  const stop = useMemo(() => dateRange.value.to(), [dateRange]);

  // Local state for filter controls
  const [filterOutOfBounds, setFilterOutOfBounds] = useState(false);
  const [selectedStatistic, setSelectedStatistic] = useState<BoxplotStatistic>('median_data');

  // Fetch sensor kit configs for the selected model
  const { data: configs } = useSensorKitConfigs({ model: filters.config_model! });

  // Calculate configIds
  const filteredConfigs = filters.config_versions
    ? configs.results.filter(config => filters.config_versions!.includes(config.version))
    : configs.results;

  const configIds = filteredConfigs.map(config => config.id);

  // Guard clause - require at least one config to be selected
  if (configIds.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-gray-500 text-center">Select a model and version to view the box plot</p>
      </Card>
    );
  }

  // Query boxplot data from API
  const { data: queryResult, isLoading, isError } = useBoxplotQuery({
    query_type: queryType,
    sensor_kit_config_ids: configIds,
    start,
    stop,
    has_current_installation: filters.has_current_installation,
    out_of_bounds_statistic: filterOutOfBounds ? selectedStatistic : undefined,
    order_by: orderBy,
    order_direction: orderDirection,
  });

  // Query bounds from API
  const { data: bounds, isSuccess: hasBounds } = useQueryBounds(queryType);

  // Query type info from API
  const { data: queryTypeInfo } = useQueryType(queryType);

  const sensorData = queryResult?.data || [];
  const skippedConfigs = queryResult?.skippedConfigs || [];

  const chartRef = useBoxPlotChart({
    sensorData,
    isLoading,
    bounds,
    displayName: queryTypeInfo?.display_name,
    unit: queryTypeInfo?.default_unit,
  });

  // Handle error state
  if (isError) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-[600px]">
          <div>Error loading data</div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {hasBounds && (
        <FilterControls
          filterOutOfBounds={filterOutOfBounds}
          setFilterOutOfBounds={setFilterOutOfBounds}
          selectedStatistic={selectedStatistic}
          setSelectedStatistic={setSelectedStatistic}
        />
      )}
      <SkippedConfigsNotice skippedConfigs={skippedConfigs} />
      {isLoading ? (
        <div className="flex items-center justify-center h-[600px] bg-white rounded-lg shadow">
          <div>Loading chart data...</div>
        </div>
      ) : (
        <ChartContainer chartRef={chartRef} sensorData={sensorData} />
      )}
    </div>
  );
};

export const SensorBoxPlot = ({ filters, queryType, orderBy, orderDirection }: SensorBoxPlotProps) => {
  const [isOpen, setIsOpen] = useState(true);

  // Guard clause - require config_model to be selected
  if (!filters.config_model) {
    return (
      <Card className="p-6">
        <p className="text-gray-500 text-center">Select a model to view the box plot</p>
      </Card>
    );
  }

  // Get display name for the chart title
  const queryTypeDisplayNames: Record<string, string> = {
    'TEMPERATURE_INSIDE_ENCLOSURE': 'Temperature Inside Enclosure',
    'RELATIVE_HUMIDITY': 'Relative Humidity',
    'WIFI_RSSI': 'WiFi RSSI'
  };

  const chartTitle = queryTypeDisplayNames[queryType] || queryType;

  return (
    <Card className="p-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto">
            <h3 className="font-semibold">{chartTitle}</h3>
            {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <SensorBoxPlotContent filters={filters} queryType={queryType} orderBy={orderBy} orderDirection={orderDirection} />
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
